import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Apple, Eye, EyeOff } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";
import { toast } from "@/lib/toast";
import { getAuthRedirectUrl, getOAuthRedirectUrl } from "@/lib/authRedirect";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline } from "@/components/primitives";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { signInWithApple } from "@/lib/appleSignIn";

type AuthStatus = "account" | "approved_no_account" | "waitlist_pending" | "unknown";

export default function Auth() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const prefilledEmail = (search.get("email") ?? "").trim().toLowerCase();
  const { session } = useAuth();
  // step 'email' = collecting email + smart-routing
  // step 'password' = email is for an account; show password field
  // step 'forgot' = password reset request
  // step 'waitlist_pending' = inline "we'll be in touch" message
  const [step, setStep] = useState<"email" | "password" | "forgot" | "waitlist_pending">("email");
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const initialSessionUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (initialSessionUserId.current === undefined) {
      initialSessionUserId.current = session?.user?.id ?? null;
      return;
    }
    const currentId = session?.user?.id ?? null;
    if (currentId && currentId !== initialSessionUserId.current) {
      nav("/", { replace: true });
    }
  }, [session, nav]);

  // First step: user enters email only. We call the auth-status-by-email
  // Edge Function to figure out where to send them.
  const handleEmailContinue = async (e: FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke<{ status: AuthStatus }>(
        "auth-status-by-email",
        { body: { email: cleanEmail } }
      );
      if (error) {
        toast.error(error.message ?? "Couldn't check email status");
        return;
      }
      const status = data?.status ?? "unknown";
      if (status === "account") {
        setStep("password");
      } else if (status === "approved_no_account") {
        nav(`/signup?email=${encodeURIComponent(cleanEmail)}`, { replace: true });
      } else if (status === "waitlist_pending") {
        setStep("waitlist_pending");
      } else {
        // unknown — push to waitlist with email autofilled
        nav(`/?email=${encodeURIComponent(cleanEmail)}`, { replace: true });
      }
    } finally {
      setBusy(false);
    }
  };

  // Second step (only after step='password'): actual password sign-in.
  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (/confirm|verif/i.test(error.message)) {
          setPendingEmail(email);
          return;
        }
        if (/invalid login|invalid email or password/i.test(error.message)) {
          toast.error("Incorrect password.");
        } else {
          toast.error(error.message ?? "Sign in failed.");
        }
        return;
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  // Forgot-password flow (entered from the password step).
  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getAuthRedirectUrl("/reset-password"),
      });
      if (error) throw error;
      toast.success("Check your inbox for a reset link.");
      setStep("password");
    } catch (err: any) {
      toast.error(err?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: pendingEmail,
      options: { emailRedirectTo: getAuthRedirectUrl("/auth/callback") },
    });
    setResending(false);
    if (error) toast.error(error.message);
    else toast.success("Verification email sent.");
  };

  const handleGoogle = async () => {
    setBusy(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: getOAuthRedirectUrl("/auth/callback") },
    });
    if (error) {
      toast.error(error.message ?? "Google sign-in failed");
      setBusy(false);
    }
  };

  const handleApple = async () => {
    setBusy(true);
    try {
      // signInWithApple() branches: native iOS uses the system Apple sheet,
      // web falls back to OAuth redirect. The native path resolves
      // synchronously (no redirect), so we MUST reset busy whether the
      // user signed in, cancelled, or errored — otherwise the email/
      // password submit button stays disabled (this was the Apple
      // reviewer's "login button was not functional" complaint).
      await signInWithApple();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Apple sign-in failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const goSignUp = () => nav("/signup");
  const goWaitlist = () => nav("/");

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid hsl(var(--ink))",
    background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 13,
    color: "hsl(var(--ink))",
    outline: "none",
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "60px 20px 40px" }}>
        {pendingEmail ? (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="caps">EST. 2026 · ED. 001</span>
              <span className="caps" style={{ color: "hsl(var(--rust))" }}>VERIFY</span>
            </div>
            <div style={{ marginTop: 40 }}>
              <span className="caps" style={{ color: "hsl(var(--rust))" }}>one more step</span>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontStyle: "italic", fontSize: 56, letterSpacing: "-0.03em", lineHeight: 0.95, color: "hsl(var(--ink))", marginTop: 6 }}>
                Check your inbox.
              </div>
              <p style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 17, color: "hsl(var(--ink))", marginTop: 14, lineHeight: 1.45, maxWidth: 320 }}>
                We sent a verification link to <strong>{pendingEmail}</strong>. Click it to activate your account, then return here to sign in.
              </p>
            </div>
            <div style={{ marginTop: 28 }}><Hairline /></div>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              style={{
                marginTop: 24, width: "100%", padding: "14px 20px",
                background: "hsl(var(--ink))", color: "hsl(var(--paper))",
                border: "1px solid hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                letterSpacing: "0.18em", textTransform: "uppercase",
                cursor: resending ? "wait" : "pointer", opacity: resending ? 0.7 : 1,
              }}
            >
              {resending ? "…" : "Resend verification email →"}
            </button>
            <div style={{ textAlign: "center", marginTop: 18 }}>
              <button
                type="button"
                onClick={() => { setPendingEmail(null); setStep("password"); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>← Back to sign in</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="caps">EST. 2026 · ED. 001</span>
              <span className="caps" style={{ color: "hsl(var(--rust))" }}>
                {step === "forgot" ? "RESET" : step === "waitlist_pending" ? "WAITLIST" : "SIGN IN"}
              </span>
            </div>

            <div style={{ marginTop: 40 }}>
              <span className="caps" style={{ color: "hsl(var(--rust))" }}>a field guide to</span>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontStyle: "italic", fontSize: 96, letterSpacing: "-0.06em", lineHeight: 0.85, color: "hsl(var(--ink))" }}>
                fig
              </div>
              <p style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 17, color: "hsl(var(--ink))", marginTop: 14, lineHeight: 1.45, maxWidth: 320 }}>
                {step === "forgot"
                  ? "Enter your email — we'll send a reset link."
                  : step === "waitlist_pending"
                  ? "You're on the list — we'll be in touch."
                  : step === "password"
                  ? "Welcome back, collector."
                  : "Welcome back. Enter your email to continue."}
              </p>
            </div>

            <div style={{ marginTop: 28 }}><Hairline /></div>

            {step === "waitlist_pending" ? (
              <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 14 }}>
                <p style={{ fontFamily: "'Fraunces', serif", fontSize: 15, lineHeight: 1.6, color: "hsl(var(--ink-2))" }}>
                  We have <strong style={{ color: "hsl(var(--ink))" }}>{email}</strong> on the waitlist but you're not approved yet. Once we send you an invite, return here to sign up.
                </p>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setEmail(""); }}
                  style={{
                    padding: "14px 20px",
                    background: "hsl(var(--ink))",
                    color: "hsl(var(--paper))",
                    border: "1px solid hsl(var(--ink))",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form
                onSubmit={step === "email" ? handleEmailContinue : step === "forgot" ? handleForgot : handlePasswordSubmit}
                style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}
              >
                <div>
                  <label htmlFor="auth-email" className="caps" style={{ display: "block", marginBottom: 6 }}>Email</label>
                  <input
                    id="auth-email"
                    name="email"
                    type="email"
                    required
                    readOnly={step === "password" || step === "forgot"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      ...inputStyle,
                      opacity: (step === "password" || step === "forgot") ? 0.7 : 1,
                      cursor: (step === "password" || step === "forgot") ? "default" : "text",
                    }}
                    autoComplete="email"
                  />
                </div>
                {step === "password" && (
                  <div>
                    <label htmlFor="auth-password" className="caps" style={{ display: "block", marginBottom: 6 }}>Password</label>
                    <div style={{ position: "relative" }}>
                      <input
                        id="auth-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ ...inputStyle, paddingRight: 44 }}
                        autoComplete="current-password"
                      />
                      <button type="button" onClick={() => setShowPassword(v => !v)}
                        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "hsl(var(--ink-2))", padding: 0, display: "flex", alignItems: "center" }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                )}

                <p style={{ fontSize: 12, color: "hsl(var(--ink))", opacity: 0.7, marginBottom: 8, textAlign: "center" }}>
                  By continuing you agree to our <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>.
                </p>
                <button type="submit" disabled={busy} style={{
                  marginTop: 8,
                  padding: "14px 20px",
                  background: "hsl(var(--ink))",
                  color: "hsl(var(--paper))",
                  border: "1px solid hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  cursor: busy ? "wait" : "pointer",
                  opacity: busy ? 0.7 : 1,
                }}>
                  {busy ? "…" :
                    step === "email" ? "Continue →" :
                    step === "forgot" ? "Send reset link →" :
                    "Sign in →"}
                </button>
              </form>
            )}

            {step === "password" && (
              <div style={{ textAlign: "center", marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setStep("forgot")}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  <span className="caps" style={{ color: "hsl(var(--rust))" }}>Forgot password?</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setPassword(""); }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>← Use a different email</span>
                </button>
              </div>
            )}
            {step === "forgot" && (
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setStep("password")}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>← Back to sign in</span>
                </button>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 14px" }}>
              <div style={{ flex: 1, borderTop: "1px dashed hsl(var(--sand-deep))" }} />
              <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>or</span>
              <div style={{ flex: 1, borderTop: "1px dashed hsl(var(--sand-deep))" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <button
                type="button"
                onClick={handleApple}
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  border: "1px solid hsl(var(--ink))",
                  background: "#000",
                  color: "#fff",
                  borderRadius: 8,
                  cursor: busy ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontWeight: 600,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                <Apple size={18} />
                Continue with Apple
              </button>

              <button
                type="button"
                onClick={handleGoogle}
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  border: "1px solid hsl(var(--ink))",
                  background: "#fff",
                  color: "#1f1f1f",
                  borderRadius: 8,
                  cursor: busy ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontWeight: 600,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                <GoogleIcon size={18} />
                Continue with Google
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: 28, display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                onClick={goSignUp}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>
                  New to FIG? <span style={{ color: "hsl(var(--rust))" }}>SIGN UP</span>
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </MobileFrame>
  );
}
